import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface ListingComparison {
  revenue: { current: number; previous: number; delta: number };
  orders: { current: number; previous: number; delta: number };
  visitors: { current: number; previous: number; delta: number };
  clicks: { current: number; previous: number; delta: number };
}

export interface ItemPromotion {
  promotionId: string | null;
  type: string;
  name: string;
  status: string;
  promoPrice: number | null;
  startTime: number | null;
  endTime: number | null;
}

export interface ListingVariation {
  id: string;
  name: string;
  sku: string | null;
  imageUrl: string | null;
  photoCount?: number;
  videoCount?: number;
  price: number | null;
  originalPrice?: number | null;
  currentPrice?: number | null;
  hasPromotion?: boolean;
  discountPercent?: number | null;
  promotions?: ItemPromotion[];
  modelId?: string | null;
  variationId: string | null;
  listingIdExt?: string;
  // ML variation metrics, or Shopee per-variation sales when hasVariationSales=true
  visitors?: number;
  orders?: number;
  revenue?: number;
  units?: number;
  conversion?: number;
  adImpressions?: number;
  adClicks?: number;
  adCtr?: number;
  adSpend?: number;
  adCpc?: number;
  adRevenue?: number;
  adOrders?: number;
  adUnits?: number;
  adRoas?: number;
  adAcos?: number;
  affiliateSales?: number;
  affiliateCommission?: number;
  affiliateItemsSold?: number;
  affiliateCount?: number;
  affiliateAcos?: number;
  paidCost?: number;
  tacos?: number;
  ratingAverage?: number | null;
  reviewsCount?: number | null;
}

export interface SellerListing {
  id: string;
  listingIdExt: string;
  marketplace: 'shopee' | 'mercadolivre';
  title: string | null;
  imageUrl: string | null;
  photoCount?: number;
  videoCount?: number;
  impressions: number;
  clicks: number;
  ctr: number;
  visitors: number;
  pageViews: number;
  addToCart: number;
  orders: number;
  revenue: number;
  units: number;
  conversion: number;
  adImpressions?: number;
  adClicks?: number;
  adCtr?: number;
  adSpend?: number;
  adCpc?: number;
  adRevenue?: number;
  adOrders?: number;
  adUnits?: number;
  adRoas?: number;
  adAcos?: number;
  adsSyncedAt?: string | null;
  affiliateSales?: number;
  affiliateCommission?: number;
  affiliateItemsSold?: number;
  affiliateCount?: number;
  affiliateAcos?: number;
  affiliateSyncedAt?: string | null;
  paidCost?: number;
  tacos?: number;
  ratingAverage?: number | null;
  reviewsCount?: number | null;
  originalPrice?: number | null;
  currentPrice?: number | null;
  hasPromotion?: boolean;
  discountPercent?: number | null;
  promotions?: ItemPromotion[];
  variations?: ListingVariation[];
  hasVariations?: boolean;
  hasVariationMetrics?: boolean;
  hasVariationSales?: boolean;
  syncedAt: string | null;
  comparison?: ListingComparison | null;
  product?: {
    id: string;
    name: string;
    sku: string | null;
    imageUrl?: string | null;
  } | null;
}

interface ListingsResponse {
  listings: SellerListing[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface UseSellerListingsOptions {
  marketplace?: string;
  connectionId?: string;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  periodDays?: number;
  page?: number;
  pageSize?: number;
}

export interface SellerListingsSyncJobStep {
  key: string;
  kind: 'metrics' | 'promotions' | 'ads' | 'affiliates';
  label: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  marketplace: string;
  connectionId: string;
  connectionName: string;
  periodDays: number;
  count?: number;
  error?: string;
}

export interface SellerListingsSyncJob {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'partial' | 'failed';
  currentStep: string | null;
  progressPercent: number;
  completedSteps: number;
  totalSteps: number;
  connectionName?: string | null;
  marketplace?: string | null;
  periods?: number[];
  steps?: SellerListingsSyncJobStep[];
  errors?: Array<{ step: string; message: string; kind?: string; connectionName?: string; periodDays?: number }>;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface SellerListingsAutoSyncConfig {
  enabled: boolean;
  intervalHours: number;
  lastRunAt?: string | null;
  nextRunAt?: string | null;
  lastJobId?: string | null;
  lastResult?: any;
}

function getHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export function useSellerListings(options: UseSellerListingsOptions = {}) {
  const { user } = useAuth();
  const [data, setData] = useState<ListingsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncingAds, setSyncingAds] = useState(false);
  const [syncingAffiliates, setSyncingAffiliates] = useState(false);
  const [syncJob, setSyncJob] = useState<SellerListingsSyncJob | null>(null);
  const [autoSyncConfig, setAutoSyncConfig] = useState<SellerListingsAutoSyncConfig | null>(null);
  const [updatingAutoSync, setUpdatingAutoSync] = useState(false);

  const fetchListings = useCallback(async () => {
    if (!user?.tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (options.marketplace) params.set('marketplace', options.marketplace);
      if (options.connectionId) params.set('connectionId', options.connectionId);
      if (options.search) params.set('search', options.search);
      if (options.sort) params.set('sort', options.sort);
      if (options.order) params.set('order', options.order);
      if (options.periodDays) params.set('periodDays', String(options.periodDays));
      if (options.page) params.set('page', String(options.page));
      if (options.pageSize) params.set('pageSize', String(options.pageSize));

      const res = await fetch(`/api/seller/listings?${params.toString()}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to fetch listings');
      const json = await res.json();
      setData(json);
    } catch (err: any) {
      setError(err?.message || 'Erro ao buscar anuncios');
    } finally {
      setLoading(false);
    }
  }, [
    user?.tenantId,
    options.marketplace,
    options.connectionId,
    options.search,
    options.sort,
    options.order,
    options.periodDays,
    options.page,
    options.pageSize,
  ]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const fetchAutoSyncConfig = useCallback(async () => {
    if (!user?.tenantId) return;
    try {
      const res = await fetch('/api/seller/listings/auto-sync', { headers: getHeaders() });
      const json = await res.json();
      if (json?.success) setAutoSyncConfig(json.config);
    } catch {}
  }, [user?.tenantId]);

  const fetchActiveSyncJob = useCallback(async () => {
    if (!user?.tenantId) return null;
    try {
      const res = await fetch('/api/seller/listings/sync-jobs/active', { headers: getHeaders() });
      const json = res.ok ? await res.json() : null;
      const latest = json?.latest as SellerListingsSyncJob | undefined;
      if (latest?.id) {
        setSyncJob(latest);
        if (['completed', 'partial', 'failed'].includes(latest.status)) {
          fetchListings();
        }
        return latest;
      }
    } catch {}
    return null;
  }, [user?.tenantId, fetchListings]);

  useEffect(() => {
    fetchAutoSyncConfig();
  }, [fetchAutoSyncConfig]);

  useEffect(() => {
    if (!user?.tenantId) return;
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    const controller = new AbortController();
    let reconnectTimer: number | null = null;
    let closed = false;
    fetchActiveSyncJob();

    const handleSyncJob = (job: SellerListingsSyncJob) => {
      if (closed || !job?.id) return;
      setSyncJob(job);
      if (['completed', 'partial', 'failed'].includes(job.status)) {
        fetchListings();
      }
    };

    const connect = async () => {
      if (closed) return;
      try {
        const res = await fetch('/api/seller/listings/sync-events', {
          headers: getHeaders(),
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (!closed) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (!line.trim()) continue;
            try {
              const payload = JSON.parse(line);
              if (payload.type === 'ping' || payload.type === 'seller_listings_sync_connected') continue;
              handleSyncJob(payload);
            } catch {}
          }
        }
      } catch {
        fetchActiveSyncJob();
      } finally {
        if (!closed) reconnectTimer = window.setTimeout(connect, 3000);
      }
    };

    connect();
    return () => {
      closed = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      controller.abort();
    };
  }, [user?.tenantId, fetchListings, fetchActiveSyncJob]);

  useEffect(() => {
    if (!user?.tenantId) return;
    const isRunning = syncJob && ['queued', 'running'].includes(syncJob.status);
    if (!isRunning) return;
    const timer = window.setInterval(() => {
      fetchActiveSyncJob();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [user?.tenantId, syncJob?.id, syncJob?.status, fetchActiveSyncJob]);

  const syncListings = useCallback(async (connectionId: string, marketplace: string = 'shopee', periodDays: number = 7) => {
    setSyncing(true);
    setError(null);
    try {
      const res = await fetch('/api/seller/listings/sync', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ connectionId, marketplace, periodDays }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Sync failed');

      // Best-effort: sync promotions/prices after metrics
      try {
        await fetch('/api/seller/listings/sync-promotions', {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ connectionId, marketplace }),
        });
      } catch {}

      await fetchListings();
    } catch (err: any) {
      setError(err?.message || 'Erro ao sincronizar anuncios');
      throw err;
    } finally {
      setSyncing(false);
    }
  }, [fetchListings]);

  const deleteListing = useCallback(async (listingIdExt: string) => {
    try {
      const res = await fetch(`/api/seller/listings/${encodeURIComponent(listingIdExt)}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Erro ao apagar');
      await fetchListings();
      return true;
    } catch (err: any) {
      setError(err?.message || 'Erro ao apagar metricas');
      return false;
    }
  }, [fetchListings]);

  const syncAds = useCallback(async (connectionId: string, marketplace: string = 'shopee', periodDays: number = 7) => {
    setSyncingAds(true);
    setError(null);
    try {
      const res = await fetch('/api/seller/listings/sync-ads', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ connectionId, marketplace, periodDays }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Sync ads failed');
      await fetchListings();
    } catch (err: any) {
      setError(err?.message || 'Erro ao sincronizar Ads');
      throw err;
    } finally {
      setSyncingAds(false);
    }
  }, [fetchListings]);

  const syncAffiliates = useCallback(async (connectionId: string, periodDays: number = 7, marketplace: string = 'shopee') => {
    setSyncingAffiliates(true);
    setError(null);
    try {
      const res = await fetch('/api/seller/listings/sync-affiliates', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ connectionId, periodDays, marketplace }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Sync affiliates failed');
      await fetchListings();
    } catch (err: any) {
      setError(err?.message || 'Erro ao sincronizar afiliados');
      throw err;
    } finally {
      setSyncingAffiliates(false);
    }
  }, [fetchListings]);

  const syncAll = useCallback(async (connectionId?: string, periods: number[] = [7, 30]) => {
    setSyncing(true);
    setSyncingAds(true);
    setSyncingAffiliates(true);
    setError(null);
    try {
      const res = await fetch('/api/seller/listings/sync-all', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ connectionId: connectionId || undefined, periods }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Sync all failed');
      setSyncJob(json.job);
      fetchActiveSyncJob();
      return json.job as SellerListingsSyncJob;
    } catch (err: any) {
      setError(err?.message || 'Erro ao iniciar sincronizacao');
      throw err;
    } finally {
      setSyncing(false);
      setSyncingAds(false);
      setSyncingAffiliates(false);
    }
  }, [fetchActiveSyncJob]);

  const updateAutoSync = useCallback(async (enabled: boolean) => {
    setUpdatingAutoSync(true);
    setError(null);
    try {
      const res = await fetch('/api/seller/listings/auto-sync', {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ enabled }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Auto sync update failed');
      setAutoSyncConfig(json.config);
      return json.config as SellerListingsAutoSyncConfig;
    } catch (err: any) {
      setError(err?.message || 'Erro ao atualizar sincronizacao automatica');
      throw err;
    } finally {
      setUpdatingAutoSync(false);
    }
  }, []);

  return {
    listings: data?.listings || [],
    total: data?.total || 0,
    page: data?.page || 1,
    pageSize: data?.pageSize || 20,
    totalPages: data?.totalPages || 0,
    loading,
    error,
    syncing,
    syncingAds,
    syncingAffiliates,
    syncJob,
    autoSyncConfig,
    updatingAutoSync,
    syncListings,
    syncAds,
    syncAffiliates,
    syncAll,
    updateAutoSync,
    deleteListing,
    refetch: fetchListings,
  };
}

